import { type ClientAndDataT } from '@ruminati/types_v2/client'
import { type PropertyT } from '@ruminati/types_v2/property'
import { type VisionSharingScopesT } from '@ruminati/types_v2/vision'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import {
  usePartnersQuery,
  useOrgMembersQuery,
  usePatchOrganisation,
  useCreateOrganisationForNewUser,
  useRequestOrganisationConnection,
  useDeleteOrganisationConnection,
  useCreateOrgMember,
  usePatchRelationship,
  useDeleteRelationship,
  useAcceptRelationship,
} from '@/queries/organisations'
import { useClientsQuery, useCreateClient } from '@/queries/partnerOrg'
import { usePropertiesQuery } from '@/queries/properties'
import { useOrganisationStore, useOrgMembersStore, usePartnersStore } from '@/stores/organisations'
import { usePartnerOrgStore } from '@/stores/partnerOrg'
import { usePropertiesStore } from '@/stores/properties'
import { useUsersStore } from '@/stores/users'
import { reportYears } from '@/utilities/reports'

export function useOrganisation() {
  const usersStore = useUsersStore()
  const organisationStore = useOrganisationStore()

  const patchOrganisationMutation = usePatchOrganisation()
  const createOrganisationForNewUserMutation = useCreateOrganisationForNewUser()

  const primaryOrg = computed(() => {
    if (organisationStore.organisations === undefined) return undefined
    return organisationStore.organisations.find(
      (o) => o.id === usersStore.profile?.userPreferences?.primaryOrganisationId,
    )
  })

  const latestConcludedReportYear = computed(() => {
    const currentOrg = organisationStore.currentOrg
    if (currentOrg?.type === 'farm') {
      const reportStartMonth = currentOrg.metadata?.report_start_month ?? 7

      const years = reportYears.filter((year) => {
        const periodEndMonth = reportStartMonth === 1 ? 12 : reportStartMonth - 1
        const periodEndYear = year
        const periodEndDate = new Date(periodEndYear, periodEndMonth, 0)
        return new Date() > periodEndDate
      })
      return years[0]
    }
    return reportYears[0]
  })

  return {
    ...organisationStore,
    primaryOrg,
    latestConcludedReportYear,
    patchOrganisationMutation,
    createOrganisationForNewUserMutation,
    ...storeToRefs(organisationStore),
  }
}

export function useVisionSharingScopes() {
  const organisationStore = useOrganisationStore()

  const availableVisionSharingScopes = computed(() => {
    if (organisationStore.currentOrg?.type !== 'vision') return []
    return organisationStore.currentOrg.preferences?.visionOrg?.availableSharingScopes ?? []
  })

  function visionOrgHasSharingScope(scope: VisionSharingScopesT) {
    return availableVisionSharingScopes.value.includes(scope)
  }

  const hasVisionEmissionsReportAccess = computed(() => {
    return (
      visionOrgHasSharingScope('emissions-report-inputs') ||
      visionOrgHasSharingScope('emissions-report-results')
    )
  })

  const hasVisionDeforestationAccess = computed(() => {
    return visionOrgHasSharingScope('deforestation')
  })

  return {
    availableVisionSharingScopes,
    visionOrgHasSharingScope,
    hasVisionEmissionsReportAccess,
    hasVisionDeforestationAccess,
  }
}

export function useOrgMembers() {
  const orgMembersStore = useOrgMembersStore()

  const orgMembersQuery = useOrgMembersQuery()

  const createOrgMemberMutation = useCreateOrgMember()
  const patchRelationshipMutation = usePatchRelationship()
  const deleteRelationshipMutation = useDeleteRelationship()
  const acceptRelationshipMutation = useAcceptRelationship()

  return {
    ...storeToRefs(orgMembersStore),
    isFetchingOrgMembers: orgMembersQuery.isFetching,

    createOrgMemberMutation,
    patchRelationshipMutation,
    deleteRelationshipMutation,
    acceptRelationshipMutation,
  }
}

export function useClients() {
  const organisationStore = useOrganisationStore()
  const clientsStore = usePartnerOrgStore()
  const propertiesStore = usePropertiesStore()

  useClientsQuery()
  usePropertiesQuery()

  const createClientMutation = useCreateClient()

  const acceptedClientProperties = computed(() => {
    return (propertiesStore.properties ?? []).filter(
      (p) => clientsStore.acceptedClientOrgIds.indexOf(p.organisationId ?? '') > -1,
    )
  })

  const acceptedClientPropertyIds = computed<string[]>(() => {
    return acceptedClientProperties.value.map((p) => p.id)
  })

  const propertiesWithClients = computed(() => {
    return propertiesStore.properties?.map((p) => ({
      p,
      client: clientsStore.clients?.find((c) => c.client.id === p.organisationId),
    }))
  })

  const acceptedPropertiesWithClients = computed(() => {
    return (acceptedClientProperties.value ?? []).map((p) => ({
      ...p,
      client: clientsStore.clients?.find((c) => c.client.id === p.organisationId),
    }))
  })

  function findClientForProperty(property: PropertyT): undefined | ClientAndDataT {
    return clientsStore.clients?.find((c) => c.client.id === property.organisationId)
  }

  return {
    ...organisationStore,
    ...clientsStore,

    createClientMutation,

    acceptedClientProperties,
    acceptedClientPropertyIds,
    propertiesWithClients,
    acceptedPropertiesWithClients,

    findClientForProperty,
    ...storeToRefs(clientsStore),
    ...storeToRefs(organisationStore),
  }
}

export function usePartners() {
  const partnersStore = usePartnersStore()

  usePartnersQuery()

  const requestOrganisationConnectionMutation = useRequestOrganisationConnection()
  const deleteOrganisationConnectionMutation = useDeleteOrganisationConnection()

  const activePartners = computed(() => partnersStore.partners?.filter((p) => p.relationship))

  const availablePartners = computed(() =>
    partnersStore.partners?.filter((p) => !p.relationship && !p.hideInPartnerOrgDirectory),
  )

  return {
    ...storeToRefs(partnersStore),
    activePartners,
    availablePartners,
    requestOrganisationConnectionMutation,
    deleteOrganisationConnectionMutation,
  }
}
